import { ChangeDetectionStrategy, Component, Input, OnInit } from '@angular/core'
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms'
import { select, Store } from '@ngrx/store'
import * as R from 'ramda'
import {
  countries,
  AlertDialogService,
  DataCheckerService,
  Destroyable,
  defaultRentalColor,
  isMainUnit,
  isParentRental,
  isRestrictingUsers,
  isSavingRental,
  isSavingTags,
  isSubunit,
  isUpdatingOwners,
  notAllNumbersValidator,
  notAllSpacesValidator,
  ParsedGooglePlace,
  Rental,
  RentalEmailNotTakenValidator,
  RentalEmailValidator,
  RentalNameNotTakenValidator,
  SaveForm,
  selectSubdomain,
  SubdomainGuard,
  untilDestroy,
  updateRental,
  UpdateRentalPayload,
  UpdateRentalStatus,
} from '@tokeet-frontend/tv3-platform'
import { distinctUntilChanged, filter } from 'rxjs/operators'
import { DeleteRentalOverlayService } from '@tv3/containers/rentals/delete-rental-overlay/delete-rental-overlay.service'
import {
  EditBookingPreferencesDialogService,
  SyncMainUnitData,
  SyncMainUnitDataConfirmService,
} from '@tokeet-frontend/rentals'

@Component({
  selector: 'app-rental-details-basic-info',
  templateUrl: './rental-details-basic-info.component.html',
  styleUrls: ['./rental-details-basic-info.component.scss'],
})
export class RentalDetailsBasicInfoComponent extends Destroyable implements OnInit {
  addressForm = this.fb.group({
    address: ['', [Validators.minLength(2), Validators.maxLength(140), notAllSpacesValidator]],
    city: ['', [Validators.minLength(2), Validators.maxLength(50), Validators.required, notAllSpacesValidator]],
    state: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    zip: ['', [Validators.minLength(2), Validators.maxLength(50), notAllSpacesValidator]],
    country: [],
    country_code: [],
  })

  form = this.fb.group({
    display_name: ['', [notAllSpacesValidator]],
    name: [
      '',
      [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(140),
        notAllNumbersValidator,
        notAllSpacesValidator,
      ],
    ],
    nickname: ['', [Validators.minLength(2), Validators.maxLength(140), notAllNumbersValidator, notAllSpacesValidator]],
    phone: ['', [Validators.required, notAllSpacesValidator]],
    email: ['', [Validators.required, Validators.minLength(3)]],
    color: [defaultRentalColor],

    address: this.addressForm,

    multi_unit_number: [],
    registration: [],
    currency: [null, [Validators.required]],
    gps: [{}],
    status: [],
  })

  get gpsCtrl() {
    return this.form.get('gps') as FormControl
  }

  _rental: Rental
  @Input() set rental(r: Rental) {
    this._rental = r
    this.setForm(r)
  }
  get rental() {
    return this._rental
  }

  get exportCalendarUrl() {
    return `https://calendars.tokeet.com/calendar/rental/${this.rental.account}/${this.rental.id}`
  }

  subDomain: string

  isChildUnitRental = false

  isSaving$ = this.store.pipe(select(isSavingRental))
  isUpdatingOwners$ = this.store.pipe(select(isUpdatingOwners))
  isRestrictingUsers$ = this.store.pipe(select(isRestrictingUsers))
  isSavingTags$ = this.store.pipe(select(isSavingTags))

  constructor(
    private fb: FormBuilder,
    private alert: AlertDialogService,
    private deleteRentalOverlayService: DeleteRentalOverlayService,
    private dataCheckerService: DataCheckerService,
    private store: Store<any>,
    private editBookingPreferencesDialogService: EditBookingPreferencesDialogService,
    private syncMainUnitDataConfirm: SyncMainUnitDataConfirmService
  ) {
    super()
    this.dataCheckerService.check([SubdomainGuard])
  }

  ngOnInit() {
    this.setForm(this.rental)

    this.store.pipe(select(selectSubdomain), untilDestroy(this)).subscribe((subDomain) => {
      this.subDomain = subDomain
    })

    this.form
      .get('status')
      .valueChanges.pipe(
        distinctUntilChanged(),
        filter((status) => status !== this.rental?.status),
        untilDestroy(this)
      )
      .subscribe((status) => {
        this.store.dispatch(UpdateRentalStatus({ rentalId: this.rental.id, status }))
      })
  }

  onEditBookingPreferences() {
    this.editBookingPreferencesDialogService.open(this.rental)
  }

  onPlaceChange(parsedPlace: ParsedGooglePlace) {
    const country = R.find((c: any) => c.id === parsedPlace.countryShort, countries)
    this.addressForm.patchValue({
      address: (parsedPlace.streetNumber ? parsedPlace.streetNumber + ' ' : '') + (parsedPlace.street || ''),
      city: parsedPlace.city,
      state: parsedPlace.state,
      country: R.pathOr('', ['name'], country),
      countryCode: R.pathOr('', ['id'], country),
      zip: parsedPlace.postCode,
    })
    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  onCityChange(parsedPlace: ParsedGooglePlace) {
    const { city } = this.addressForm.getRawValue()

    const googleCity = (parsedPlace.city || '')?.trim()
    this.addressForm.patchValue({
      city: googleCity ? googleCity : city,
    })

    const country = R.find((c: any) => c.id === parsedPlace.countryShort, countries)
    if (country) {
      this.addressForm.patchValue({
        country: country.name,
        country_code: country.id,
      })
    }

    this.gpsCtrl.patchValue(parsedPlace.gps)
  }

  onDelete() {
    if (isParentRental(this.rental) || isMainUnit(this.rental)) {
      this.alert.open({
        title: $localize`Warning`,
        body: $localize`You cannot delete a parent rental or main unit.`,
      })
      return
    }
    this.deleteRentalOverlayService.open(this.rental.id)
  }

  save() {
    this.onSave(this.form)
  }

  @SaveForm()
  onSave(form: FormGroup) {
    const { status, registration, ...others } = form.getRawValue()
    const payload: UpdateRentalPayload = {
      ...others,
      email: `${others.email}@${this.subDomain}`,
      attributes: {
        ...this.rental.attributes,
        registration,
      },
    }

    this.store.dispatch(updateRental({ payload, rentalId: this.rental.id }))

    this.syncMainUnitDataConfirm.tryToSyncData(this.rental, SyncMainUnitData.Basic).subscribe()
  }

  private setForm(rental: Rental) {
    const nameCtrl = this.form.get('name')
    const emailCtrl = this.form.get('email')

    nameCtrl.clearAsyncValidators()
    emailCtrl.clearAsyncValidators()

    nameCtrl.addAsyncValidators(RentalNameNotTakenValidator.createValidator(this.store, rental.id))
    emailCtrl.addAsyncValidators([
      RentalEmailNotTakenValidator.createValidator(this.store, rental.id),
      RentalEmailValidator.createValidator(this.store),
    ])
    nameCtrl.updateValueAndValidity()
    emailCtrl.updateValueAndValidity()

    this.form.patchValue({
      name: rental.name,
      nickname: rental.nickname,
      display_name: rental.displayName,
      phone: rental.phone,
      email: R.pipe(R.when(R.is(Number), R.toString), R.split('@'), R.head)(rental.email),
      address: {
        address: R.pathOr('', ['address', 'address'], rental),
        city: R.pathOr('', ['address', 'city'], rental),
        state: R.pathOr('', ['address', 'state'], rental),
        country: R.pathOr('', ['address', 'country'], rental),
        country_code: R.pathOr('', ['address', 'countryCode'], rental),
        zip: R.pathOr('', ['address', 'zip'], rental),
      },
      color: rental.color,
      gps: rental.gps || {},
      registration: (rental.attributes as any)?.registration,
      currency: rental.currency || {},
      status: rental.status || 'ready',
      multi_unit_number: rental.multi_unit_number,
    })

    this.isChildUnitRental = isSubunit(this.rental)
    if (this.isChildUnitRental) {
      this.disableChildUnitFields()
    } else {
      this.enableChildUnitFields()
    }
  }

  private disableChildUnitFields() {
    this.form.get('display_name').disable()
    this.form.get('phone').disable()
    this.form.get('email').disable()
    this.form.get('currency').disable()
    this.form.get('address').disable()
  }

  private enableChildUnitFields() {
    this.form.get('display_name').enable()
    this.form.get('phone').enable()
    this.form.get('email').enable()
    this.form.get('currency').enable()
    this.form.get('address').enable()
  }
}
